/**
 * LM: product options (personalization). See blocks/lm-product-options.liquid.
 * Shows a Choice's own fields when it's on anything but its off choice, shows as many name fields as a
 * Count asks for, and disables every hidden field so it doesn't reach the order. Sets the hidden
 * `_is_customized` property to "yes" when any shown Choice is on (lm-hub reads it).
 * During a Loop on-store exchange (Loop's script puts `loop-returns-activated` on <body>, possibly after
 * load) the options are hidden by CSS and every field is disabled, so none can block or reach the order.
 */
class LmProductOptions extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {MutationObserver | undefined} */
  #exchangeObserver;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    this.addEventListener('change', () => this.update(), { signal });
    this.addEventListener('input', (event) => {
      if (event.target instanceof HTMLElement && event.target.matches('[data-lm-count]')) this.update();
    }, { signal });
    this.#exchangeObserver = new MutationObserver(() => this.update());
    this.#exchangeObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    this.update();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#exchangeObserver?.disconnect();
  }

  update() {
    const exchange = document.body.classList.contains('loop-returns-activated');
    let customized = false;

    // Document order, so an outer Choice is settled before the ones inside it.
    for (const control of this.querySelectorAll('[data-lm-choice], [data-lm-count]')) {
      const shown = !exchange && !control.parentElement?.closest('[hidden]');

      if (control instanceof HTMLSelectElement) {
        const off = control.dataset.lmOff ?? '';
        const on = shown && off !== '' && control.value !== off;
        const reveal = control.closest('.lm-option--choice')?.querySelector(':scope > .lm-option__reveal');
        if (reveal instanceof HTMLElement) reveal.hidden = !on;
        if (on) customized = true;
      } else if (control instanceof HTMLInputElement) {
        const count = shown ? this.#clamp(control) : 0;
        const names = control.closest('.lm-option--count')?.querySelectorAll('.lm-option__name') ?? [];
        names.forEach((name, index) => {
          if (name instanceof HTMLElement) name.hidden = index >= count;
        });
      }
    }

    for (const field of this.querySelectorAll('input:not([data-lm-customized]), select')) {
      if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement)) continue;
      field.disabled = exchange || Boolean(field.closest('[hidden]'));
    }

    const flag = this.querySelector('[data-lm-customized]');
    if (flag instanceof HTMLInputElement) flag.value = customized ? 'yes' : 'no';
  }

  /**
   * Keeps a Count between 0 and its max and returns it.
   * @param {HTMLInputElement} input
   */
  #clamp(input) {
    const max = Number(input.max) || 0;
    let value = Math.trunc(Number(input.value));
    if (!Number.isFinite(value) || value < 0) value = 0;
    if (value > max) value = max;
    if (input.value !== '' && String(value) !== input.value) input.value = String(value);
    return value;
  }
}

if (!customElements.get('lm-product-options')) customElements.define('lm-product-options', LmProductOptions);
